import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormGroup, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { TextBoxComponent, TextBoxModule } from '@progress/kendo-angular-inputs';
import { BaseComponent } from '@core/models/base-component.model';
import { BaseControl } from '../../ui/controls/base-control.model';
import { PasswordRequirementValidator, PasswordStrength } from '../../utilities/password-requirement-validator';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { PasswordControl } from '../../ui/controls/password-control.model';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { eyeIcon, SVGIcon } from '@progress/kendo-svg-icons';

@Component({
    selector: 'app-password',
    imports: [TextBoxModule, ButtonModule, FormsModule, FontAwesomeModule, CommonModule, TranslateModule],
    providers: [
        TranslateService,
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PasswordComponent
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: PasswordComponent
        }
    ],
    templateUrl: './password.component.html',
    styleUrl: './password.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PasswordComponent extends BaseComponent implements OnInit, ControlValueAccessor, Validator {

  @Input() control: BaseControl;
  @Input() form: FormGroup;

  @ViewChild("textbox") public textbox: TextBoxComponent;

  passwordControl: PasswordControl;

  eyeIcon: SVGIcon = eyeIcon;

  value: string;
  onChange: (value: any) => void;
  touched: boolean = false;
  onTouched = () => { };
  disabled: boolean = false;

  validator: PasswordRequirementValidator;
  result: PasswordStrength;
  showValidationSection: boolean = false;
  performValidation: boolean;
  strengthLevel: string = "Very Weak";
  strengthPercentage: string = "25%";
  strengthClass: string = "password-strength-danger";
  strengthResultClass: string = "password-strength-result-label-danger";

  checkIcon = faCheck;
  timesIcon = faTimes;

  constructor(private cd: ChangeDetectorRef) { super(); }

  ngOnInit() {
    this.passwordControl = this.control as PasswordControl;
    this.validator = new PasswordRequirementValidator();
    this.performValidation = this.passwordControl.performValidation;

    // Setup listener for Confirm Password field
    this.form.get(this.passwordControl.confirmPasswordFieldId)?.valueChanges.subscribe(value => {
      this.passwordChanged(this.value, value);
    });
  }

  writeValue(writtenValue: any): void {
    this.value = writtenValue;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const errors: any = {};
    if (this.passwordControl.isRequired && !this.value) {
      errors['error'] = 'Required field';
    }

    if (this.result && !this.result.isValid) {
      errors['error'] = 'Failed Complexity Requirements';
    }

    if (Object.keys(errors).length > 0) {
      return errors;
    }

    return null;
  }

  toggleVisibility() {
    var e = window.event;
    e?.preventDefault();
    const inputEl = this.textbox.input.nativeElement;
    if (inputEl.type == "password") {
      inputEl.type = "text";
    } else {
      inputEl.type = "password";
    }
    this.cd.detectChanges();
  }

  passwordChanged(value: string, confirmPassword: string) {

    if (this.performValidation) {
      this.showValidationSection = value.length > 0;

      this.result = this.validator.evaluate(value, confirmPassword);

      var strength = this.result.strength;

      if (strength < 2) {
        this.strengthLevel = "Very Weak";
        this.strengthPercentage = "25%";
        this.strengthClass = "password-strength-danger";
        this.strengthResultClass = "password-strength-result-label-danger";
      } else if (strength == 2) {
        this.strengthLevel = "Weak";
        this.strengthPercentage = "50%";
        this.strengthClass = "password-strength-warning";
        this.strengthResultClass = "password-strength-result-label-warning";
      } else if (strength == 3 || strength == 4) {
        this.strengthLevel = "Strong";
        this.strengthPercentage = "75%";
        this.strengthClass = "password-strength-information";
        this.strengthResultClass = "password-strength-result-label-info";
      } else if (strength == 5) {
        this.strengthLevel = "Very Strong";
        this.strengthPercentage = "100%";
        this.strengthClass = "password-strength-success";
        this.strengthResultClass = "password-strength-result-label-success";
      }
    }

    this.value = value;
    this.onChange(value);
    this.cd.detectChanges();
  }

}
